import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LongTermRentalState } from 'src/app/module/long-term-rental/state/long-term-rental.state';
import { IApproachState } from 'src/app/module/order/interface/approach-state.interface';
import {
  AnyApproach,
  ApproachKey,
  CommonApproachKeys,
  ComparableTableDataSource,
  PropertyMap,
  SecondarySearchFilter,
  ValuationType,
} from 'src/app/module/order/interface/order.interface';
import { CostApproachState } from 'src/app/module/order/state/cost-approach.state';
import { OrderState } from 'src/app/module/order/state/order.state';
import { SalesApproachState } from 'src/app/module/order/state/sales-approach.state';
import { UnitOpScope } from 'src/app/module/property/interface/property.interface';
import { ShortTermRentalState } from 'src/app/module/short-term-rental/state/short-term-rental.state';
import { BaseApproachSetting } from 'src/app/shared/interface/company.interface';
import { Scenario } from '../../scenario/interface/scenario.interface';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { SyncScope } from '../../sync/interface/sync.interface';
import { SyncState } from '../../sync/state/sync.state';

type OrderApproach = Pick<
  Scenario,
  ApproachKey.salesComparison | ApproachKey.costApproach | ApproachKey.shortTermRental | ApproachKey.longTermRental
>;

@Injectable({
  providedIn: 'root',
})
export class ApproachFactoryState implements IApproachState {
  constructor(
    private orderState: OrderState,
    private salesApproachState: SalesApproachState,
    private costApproachState: CostApproachState,
    private shortTermRentalState: ShortTermRentalState,
    private longTermRentalState: LongTermRentalState,
    private scenarioState: ScenarioState,
    private syncState: SyncState
  ) {}

  get activeApproachSetting(): BaseApproachSetting | undefined {
    return this.getApproachSettingOnType(this.orderState.activeValuationType);
  }

  getApproachSettingOnType(valuationType: ValuationType | null): BaseApproachSetting | undefined {
    switch (valuationType) {
      case ValuationType.SALES:
        return this.orderState.orderSetting?.salesComparison;

      case ValuationType.COST:
        return this.orderState.orderSetting?.costApproach;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.orderState.orderSetting?.shortTermRental;

      case ValuationType.LONG_TERM_RENTAL:
        return this.orderState.orderSetting?.longTermRental;

      default:
        return this.orderState.orderSetting?.salesComparison;
    }
  }

  get comparableList$(): Observable<ComparableTableDataSource[]> | undefined {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.comparableList$;

      case ValuationType.COST:
        return this.costApproachState.comparableList$;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.comparableList$;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.comparableList$;

      default:
        return;
    }
  }

  get comparableList(): ComparableTableDataSource[] {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.comparableList;

      case ValuationType.COST:
        return this.costApproachState.comparableList;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.comparableList;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.comparableList;

      default:
        return [];
    }
  }

  set comparableList(order: ComparableTableDataSource[]) {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        this.salesApproachState.comparableList = order;
        break;

      case ValuationType.COST:
        this.costApproachState.comparableList = order;
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this.shortTermRentalState.comparableList = order;
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this.longTermRentalState.comparableList = order;
        break;

      default:
        break;
    }
  }

  get selectedComparableMap$(): Observable<PropertyMap> | undefined {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.selectedComparableMap$;

      case ValuationType.COST:
        return this.costApproachState.selectedComparableMap$;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.selectedComparableMap$;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.selectedComparableMap$;

      default:
        return;
    }
  }

  get selectedComparableMap(): PropertyMap {
    return this.getSelectedComparableMapOnType(this.orderState.activeValuationType);
  }

  set selectedComparableMap(value: PropertyMap) {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        this.salesApproachState.selectedComparableMap = value;
        break;

      case ValuationType.COST:
        this.costApproachState.selectedComparableMap = value;
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this.shortTermRentalState.selectedComparableMap = value;
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this.longTermRentalState.selectedComparableMap = value;
        break;

      default:
        break;
    }
  }

  /** Last-synced (baseline) selected comparable map for the active approach. */
  get syncedSelectedComparableMap(): PropertyMap {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.syncState.syncedSelectedComparableMap;

      case ValuationType.COST:
        return this.syncState.syncedCostApproachSelectedComparableMap;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.syncState.syncedShortTermRentSelectedComparableMap;

      case ValuationType.LONG_TERM_RENTAL:
        return this.syncState.syncedLongTermRentSelectedComparableMap;

      default:
        return {};
    }
  }

  set syncedSelectedComparableMap(value: PropertyMap) {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        this.syncState.syncedSelectedComparableMap = value;
        break;

      case ValuationType.COST:
        this.syncState.syncedCostApproachSelectedComparableMap = value;
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this.syncState.syncedShortTermRentSelectedComparableMap = value;
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this.syncState.syncedLongTermRentSelectedComparableMap = value;
        break;

      default:
        break;
    }
  }

  get comparableListLength$(): Observable<number> | undefined {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.comparableListLength$;

      case ValuationType.COST:
        return this.costApproachState.comparableListLength$;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.comparableListLength$;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.comparableListLength$;

      default:
        return;
    }
  }

  get comparableListLength(): number {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.comparableListLength;

      case ValuationType.COST:
        return this.costApproachState.comparableListLength;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.comparableListLength;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.comparableListLength;

      default:
        return 0;
    }
  }

  set comparableListLength(length: number) {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        this.salesApproachState.comparableListLength = length;
        break;

      case ValuationType.COST:
        this.costApproachState.comparableListLength = length;
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this.shortTermRentalState.comparableListLength = length;
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this.longTermRentalState.comparableListLength = length;
        break;

      default:
        break;
    }
  }

  get secondarySearchFilter(): SecondarySearchFilter | null | undefined {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.secondarySearchFilter;

      case ValuationType.COST:
        return this.costApproachState.secondarySearchFilter;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.secondarySearchFilter;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.secondarySearchFilter;

      default:
        return null;
    }
  }

  set secondarySearchFilter(filter: SecondarySearchFilter | null) {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        this.salesApproachState.secondarySearchFilter = filter;
        break;

      case ValuationType.COST:
        this.costApproachState.secondarySearchFilter = filter;
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this.shortTermRentalState.secondarySearchFilter = filter;
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this.longTermRentalState.secondarySearchFilter = filter;
        break;

      default:
        break;
    }
  }

  get activeApproachKey(): keyof OrderApproach {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return ApproachKey.salesComparison;

      case ValuationType.COST:
        return ApproachKey.costApproach;

      case ValuationType.SHORT_TERM_RENTAL:
        return ApproachKey.shortTermRental;

      case ValuationType.LONG_TERM_RENTAL:
        return ApproachKey.longTermRental;

      default:
        return ApproachKey.salesComparison;
    }
  }

  get approachKeyBasedOnReportType(): keyof OrderApproach {
    switch (this.orderState.reportType) {
      case 'desktopAppraisalReport':
        return ApproachKey.salesComparison;

      case 'shortTermRentalReport':
        return ApproachKey.shortTermRental;

      default:
        return ApproachKey.salesComparison;
    }
  }

  get activeApproachPage(): string {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return 'sales-approach';

      case ValuationType.COST:
        return 'cost-approach';

      case ValuationType.SHORT_TERM_RENTAL:
        return 'short-term-rental';

      case ValuationType.LONG_TERM_RENTAL:
        return 'long-term-rental';

      default:
        return 'sales-approach';
    }
  }

  getOrderValue<T extends CommonApproachKeys>(key: T): AnyApproach[T] {
    const activeScenario = this.scenarioState.activeScenario;

    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return structuredClone(activeScenario?.salesComparison?.[key]);

      case ValuationType.COST:
        return structuredClone(activeScenario?.costApproach?.[key]);

      case ValuationType.SHORT_TERM_RENTAL:
        return structuredClone(activeScenario?.shortTermRental?.[key]);

      case ValuationType.LONG_TERM_RENTAL:
        return structuredClone(activeScenario?.longTermRental?.[key]);

      default:
        return;
    }
  }

  setOrderValue<T extends CommonApproachKeys>(key: T, value: AnyApproach[T]): void {
    const activeScenario = structuredClone(this.scenarioState.activeScenario);

    if (this.orderState.activeValuationType == ValuationType.SALES && activeScenario?.salesComparison) {
      activeScenario.salesComparison[key] = value;
    } else if (this.orderState.activeValuationType == ValuationType.COST && activeScenario?.costApproach) {
      activeScenario.costApproach[key] = value;
    } else if (
      this.orderState.activeValuationType == ValuationType.SHORT_TERM_RENTAL &&
      activeScenario?.shortTermRental
    ) {
      activeScenario.shortTermRental[key] = value;
    } else if (
      this.orderState.activeValuationType == ValuationType.LONG_TERM_RENTAL &&
      activeScenario?.longTermRental
    ) {
      activeScenario.longTermRental[key] = value;
    }

    this.scenarioState.activeScenario = activeScenario;
  }

  /**
   * Returns the comparable id order to use when rendering comps.
   * Uses the API-returned order when the active approach has
   * `selectedComparableSortBy` set, otherwise falls back to the
   * scenario's `selectedComparableList`.
   */
  getOrderedSelectedComparableIds(): string[] {
    if (this.activeApproachSetting?.selectedComparableSortBy) {
      return this._getOrderedComparableIdsOnType(this.orderState.activeValuationType) ?? [];
    }
    return this.getOrderValue('selectedComparableList')?.map(({ id }) => id) ?? [];
  }

  setOrderedSelectedComparableIdsOnType(valuationType: ValuationType | null, ids: string[]): void {
    switch (valuationType) {
      case ValuationType.SALES:
        this.salesApproachState.orderedSelectedComparableIds = ids;
        break;

      case ValuationType.COST:
        this.costApproachState.orderedSelectedComparableIds = ids;
        break;

      case ValuationType.SHORT_TERM_RENTAL:
        this.shortTermRentalState.orderedSelectedComparableIds = ids;
        break;

      case ValuationType.LONG_TERM_RENTAL:
        this.longTermRentalState.orderedSelectedComparableIds = ids;
        break;

      default:
        break;
    }
  }

  private _getOrderedComparableIdsOnType(valuationType: ValuationType | null): string[] {
    switch (valuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.orderedSelectedComparableIds;

      case ValuationType.COST:
        return this.costApproachState.orderedSelectedComparableIds;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.orderedSelectedComparableIds;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.orderedSelectedComparableIds;

      default:
        return [];
    }
  }

  getSelectedComparableMapOnType(valuationType: ValuationType | null): PropertyMap {
    switch (valuationType) {
      case ValuationType.SALES:
        return this.salesApproachState.selectedComparableMap;

      case ValuationType.COST:
        return this.costApproachState.selectedComparableMap;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.shortTermRentalState.selectedComparableMap;

      case ValuationType.LONG_TERM_RENTAL:
        return this.longTermRentalState.selectedComparableMap;

      default:
        return {};
    }
  }

  get activeCompsSyncStateScope():
    | SyncScope.SALES_SELECTED_COMPS
    | SyncScope.COST_SELECTED_COMPS
    | SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS
    | SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS
    | null {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return SyncScope.SALES_SELECTED_COMPS;

      case ValuationType.COST:
        return SyncScope.COST_SELECTED_COMPS;

      case ValuationType.SHORT_TERM_RENTAL:
        return SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS;

      case ValuationType.LONG_TERM_RENTAL:
        return SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS;

      default:
        return SyncScope.SALES_SELECTED_COMPS;
    }
  }

  /** Identifies the state object holding the active approach's selected comparables for unit operations. */
  get activeCompsUnitOpScope(): UnitOpScope | null {
    switch (this.orderState.activeValuationType) {
      case ValuationType.SALES:
        return 'SALES_COMPS';

      case ValuationType.COST:
        return 'COST_COMPS';

      case ValuationType.SHORT_TERM_RENTAL:
        return 'STR_COMPS';

      case ValuationType.LONG_TERM_RENTAL:
        return 'LTR_COMPS';

      default:
        return null;
    }
  }

  getDerivedValueOnType(valuationType: ValuationType | null): any {
    switch (valuationType) {
      case ValuationType.SALES:
        return this.scenarioState.activeScenario?.salesComparison?.derivedPrice;

      case ValuationType.COST:
        return this.scenarioState.activeScenario?.costApproach?.costOfBuildingDetail?.costApproachValue;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.scenarioState.activeScenario?.shortTermRental?.derivedValue;

      case ValuationType.LONG_TERM_RENTAL:
        return this.scenarioState.activeScenario?.longTermRental?.derivedValue;

      default:
        return null;
    }
  }

  getScenarioDerivedValueOnType(scenarioId: string, valuationType: ValuationType | null): any {
    switch (valuationType) {
      case ValuationType.SALES:
        return this.scenarioState.scenarioMap?.[scenarioId]?.salesComparison?.derivedPrice;

      case ValuationType.COST:
        return this.scenarioState.scenarioMap?.[scenarioId]?.costApproach?.costOfBuildingDetail?.costApproachValue;

      case ValuationType.SHORT_TERM_RENTAL:
        return this.scenarioState.scenarioMap?.[scenarioId]?.shortTermRental?.derivedValue;

      case ValuationType.LONG_TERM_RENTAL:
        return this.scenarioState.scenarioMap?.[scenarioId]?.longTermRental?.derivedValue;

      default:
        return null;
    }
  }
}
